import { Injectable } from '@angular/core';

export type LeadsSurfaceView = 'list' | 'board';

export interface LeadsSurfaceContext {
  id: 'leads';
  view: LeadsSurfaceView;
}

export interface LeadEmailComposerSurfaceContext {
  id: 'lead-email-composer';
  leadId: number;
}

export interface LeadTableViewRule {
  field: LeadTableFilterField;
  op: string;
  value: string;
}

export interface LeadTableListSort {
  field: LeadTableSortField;
  direction: 'asc' | 'desc';
}

export interface LeadTableViewAction {
  type: 'lead-table-view';
  target: 'leads';
  rules: readonly LeadTableViewRule[];
  listSort: LeadTableListSort | null;
}

export interface LeadEmailDraftAction {
  type: 'lead-email-draft';
  target: 'lead-email-composer';
  leadId: number;
  subject: string;
  body: string;
}

export interface SurfaceActionApplyResult {
  /** Opaque to the app shell; only the target which created it can restore it. */
  restoreState: unknown;
  note?: string;
}

export interface CurrentSurfaceActionTarget {
  readonly surface: LeadsSurfaceContext;
  apply(action: LeadTableViewAction): SurfaceActionApplyResult | null;
  restore(state: unknown): boolean;
}

export interface LeadEmailComposerActionTarget {
  readonly surface: LeadEmailComposerSurfaceContext;
  applyDraft(action: LeadEmailDraftAction): boolean;
}

type RegisteredSurfaceTarget = CurrentSurfaceActionTarget | LeadEmailComposerActionTarget;

/** A receipt is tied to one mounted target instance. Navigating away makes it
 * stale instead of calling back into a destroyed routed component. */
export interface SurfaceActionReceipt {
  readonly registrationId: number;
  readonly target: 'leads';
  readonly restoreState: unknown;
  readonly note?: string;
}

const FILTERS = {
  title: { kind: 'text', ops: ['contains', 'eq'] },
  stage: { kind: 'choice', ops: ['eq'] },
  origin: { kind: 'choice', ops: ['eq'] },
  salesRep: { kind: 'choice', ops: ['eq'] },
  routeFit: { kind: 'choice', ops: ['eq'] },
  address: { kind: 'text', ops: ['contains', 'eq'] },
  city: { kind: 'text', ops: ['contains', 'eq'] },
  state: { kind: 'text', ops: ['contains', 'eq'] },
  postalCode: { kind: 'text', ops: ['contains', 'eq'] },
  nextActionType: { kind: 'text', ops: ['contains', 'eq'] },
  // A closed list since the categories became rows, so eq alone - the same
  // contract WorkItemFilterFields.Kinds declares on the server.
  businessTypes: { kind: 'choice', ops: ['eq'] },
  // A source kind - osm, google, user - behind an accepted link.
  sources: { kind: 'choice', ops: ['eq'] },
  sourceCount: { kind: 'number', ops: ['gte', 'lte', 'eq'], min: 0, max: 1_000_000 },
  score: { kind: 'number', ops: ['gte', 'lte', 'eq'], min: 0, max: 100 },
  contribution: {
    kind: 'number', ops: ['gte', 'lte', 'eq'],
    min: -1_000_000_000_000, max: 1_000_000_000_000,
  },
  probability: { kind: 'number', ops: ['gte', 'lte', 'eq'], min: 0, max: 1 },
  nextActionDue: { kind: 'date', ops: ['after', 'before'] },
  discoveredAt: { kind: 'date', ops: ['after', 'before'] },
} as const;

export type LeadTableFilterField = keyof typeof FILTERS;

const SORT_FIELDS = [
  'title', 'boardColumn', 'origin', 'sourceCount', 'qualificationScore',
  'contributionBase', 'nextActionDue', 'probability', 'discoveredAt',
] as const;

export type LeadTableSortField = typeof SORT_FIELDS[number];

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const hasOnly = (value: Record<string, unknown>, allowed: readonly string[]): boolean =>
  Object.keys(value).every(key => allowed.includes(key));

const validDate = (value: string): boolean => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
};

const validRuleValue = (
  field: LeadTableFilterField,
  value: string,
): boolean => {
  if (!value || value.length > 500 || value !== value.trim()) return false;
  const descriptor = FILTERS[field];
  if (descriptor.kind === 'number') {
    if (!/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(value)) return false;
    const number = Number(value);
    return Number.isFinite(number)
      && number >= descriptor.min
      && number <= descriptor.max;
  }
  return descriptor.kind !== 'date' || validDate(value);
};

/** Browser-side narrowing for the trusted SSE frame. Assistant prose never
 * enters this function; the conversation service calls it only for an object
 * whose frame type is `lead-table-view`. */
export function parseLeadTableViewAction(value: unknown): LeadTableViewAction | null {
  if (!isObject(value)
      || !hasOnly(value, ['type', 'target', 'rules', 'listSort'])
      || value['type'] !== 'lead-table-view'
      || value['target'] !== 'leads'
      || !Array.isArray(value['rules'])
      || value['rules'].length > 10) {
    return null;
  }

  const rules: LeadTableViewRule[] = [];
  const seen = new Set<string>();
  for (const candidate of value['rules']) {
    if (!isObject(candidate)
        || !hasOnly(candidate, ['field', 'op', 'value'])
        || typeof candidate['field'] !== 'string'
        || !(candidate['field'] in FILTERS)
        || typeof candidate['op'] !== 'string'
        || typeof candidate['value'] !== 'string') {
      return null;
    }
    const field = candidate['field'] as LeadTableFilterField;
    if (seen.has(field)
        || !(FILTERS[field].ops as readonly string[]).includes(candidate['op'])
        || !validRuleValue(field, candidate['value'])) {
      return null;
    }
    seen.add(field);
    rules.push({ field, op: candidate['op'], value: candidate['value'] });
  }

  let listSort: LeadTableListSort | null = null;
  const candidateSort = value['listSort'];
  if (candidateSort !== null && candidateSort !== undefined) {
    if (!isObject(candidateSort)
        || !hasOnly(candidateSort, ['field', 'direction'])
        || typeof candidateSort['field'] !== 'string'
        || !(SORT_FIELDS as readonly string[]).includes(candidateSort['field'])
        || (candidateSort['direction'] !== 'asc' && candidateSort['direction'] !== 'desc')) {
      return null;
    }
    listSort = {
      field: candidateSort['field'] as LeadTableSortField,
      direction: candidateSort['direction'],
    };
  }

  return { type: 'lead-table-view', target: 'leads', rules, listSort };
}

/** Browser-side narrowing for the trusted email-draft SSE frame. The target
 * identity is part of the action, so a draft for one lead cannot land in a
 * different lead's composer after navigation. */
export function parseLeadEmailDraftAction(value: unknown): LeadEmailDraftAction | null {
  if (!isObject(value)
      || !hasOnly(value, ['type', 'target', 'leadId', 'subject', 'body'])
      || value['type'] !== 'lead-email-draft'
      || value['target'] !== 'lead-email-composer'
      || typeof value['leadId'] !== 'number'
      || !Number.isSafeInteger(value['leadId'])
      || value['leadId'] <= 0
      || typeof value['subject'] !== 'string'
      || !value['subject']
      || value['subject'] !== value['subject'].trim()
      || value['subject'].length > 256
      || typeof value['body'] !== 'string'
      || !value['body']
      || value['body'] !== value['body'].trim()
      || value['body'].length > 10_000) {
    return null;
  }

  return {
    type: 'lead-email-draft',
    target: 'lead-email-composer',
    leadId: value['leadId'],
    subject: value['subject'],
    body: value['body'],
  };
}

/** Root-owned bridge between the persistent command bar and whichever routed
 * page is currently mounted. It holds no page state and performs no action on
 * receipt; the user must explicitly Apply a proposal from the transcript. */
@Injectable({ providedIn: 'root' })
export class CurrentSurfaceActionService {
  private nextRegistrationId = 0;
  private registrations: Array<{
    id: number;
    target: RegisteredSurfaceTarget;
  }> = [];

  private get current(): { id: number; target: RegisteredSurfaceTarget } | undefined {
    return this.registrations[this.registrations.length - 1];
  }

  register(target: RegisteredSurfaceTarget): () => void {
    const registration = { id: ++this.nextRegistrationId, target };
    this.registrations.push(registration);
    return () => {
      this.registrations = this.registrations.filter(item => item.id !== registration.id);
    };
  }

  canApply(action: LeadTableViewAction): boolean {
    return this.current?.target.surface.id === action.target;
  }

  apply(action: LeadTableViewAction): SurfaceActionReceipt | null {
    const registration = this.current;
    if (!registration || !this.isLeadTableTarget(registration.target)
        || registration.target.surface.id !== action.target) return null;
    const result = registration.target.apply(action);
    return result ? {
      registrationId: registration.id,
      target: action.target,
      restoreState: result.restoreState,
      note: result.note,
    } : null;
  }

  canRestore(receipt: SurfaceActionReceipt): boolean {
    return this.current?.id === receipt.registrationId
      && this.current.target.surface.id === receipt.target;
  }

  restore(receipt: SurfaceActionReceipt): boolean {
    const target = this.current?.target;
    if (!this.canRestore(receipt) || !target || !this.isLeadTableTarget(target)) return false;
    return target.restore(receipt.restoreState);
  }

  canApplyEmailDraft(action: LeadEmailDraftAction): boolean {
    const target = this.current?.target;
    return !!target
      && this.isLeadEmailTarget(target)
      && target.surface.id === action.target
      && target.surface.leadId === action.leadId;
  }

  applyEmailDraft(action: LeadEmailDraftAction): boolean {
    const target = this.current?.target;
    return !!target
      && this.isLeadEmailTarget(target)
      && target.surface.id === action.target
      && target.surface.leadId === action.leadId
      && target.applyDraft(action);
  }

  private isLeadTableTarget(
    target: RegisteredSurfaceTarget,
  ): target is CurrentSurfaceActionTarget {
    return target.surface.id === 'leads';
  }

  private isLeadEmailTarget(
    target: RegisteredSurfaceTarget,
  ): target is LeadEmailComposerActionTarget {
    return target.surface.id === 'lead-email-composer';
  }
}
